import { createTheme } from "@mui/material/styles";

// Material 3 roles with a modern OLED Cherry Dark palette for optimal manga art contrast.
export const yomiriColors = {
  surface: "#0c0a0e",
  containerLow: "#141117",
  container: "#1b1720",
  containerHigh: "#25202c",
  containerHighest: "#312a39",
  onSurface: "#f4edf2",
  onSurfaceVariant: "#ab9ea7",
  outline: "rgba(255, 255, 255, 0.14)",
  outlineVariant: "rgba(255, 255, 255, 0.07)",
  primary: "#ff2e63",
  onPrimary: "#ffffff",
  primaryContainer: "#4a1020",
  onPrimaryContainer: "#ffd7df",
  error: "#ff5449",
  success: "#4ade80",
};

const c = yomiriColors;
export const yomiriTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: c.primary, contrastText: c.onPrimary },
    secondary: { main: c.onSurfaceVariant },
    error: { main: c.error },
    success: { main: c.success },
    background: { default: c.surface, paper: c.containerLow },
    text: { primary: c.onSurface, secondary: c.onSurfaceVariant },
    divider: c.outlineVariant,
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontSize: "2rem", fontWeight: 700, letterSpacing: "-.025em" },
    h2: { fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-.02em" },
    h4: { fontSize: "1.75rem", fontWeight: 600, letterSpacing: "-.015em" },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: ".01em" },
    body1: { fontSize: "1rem", lineHeight: 1.6 },
    body2: { fontSize: ".875rem", lineHeight: 1.5 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ":root": Object.fromEntries(Object.entries(c).map(([key, value]) => [`--y-${key}`, value])),
        "html, body, #root": { height: "100%", margin: 0 },
        body: { backgroundColor: c.surface, color: c.onSurface },
        "*": { scrollbarWidth: "thin", scrollbarColor: `${c.outlineVariant} transparent` },
        ":focus-visible": { outline: `2px solid ${c.primary}`, outlineOffset: 2 },
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            animationDuration: ".01ms !important",
            transitionDuration: ".01ms !important",
            scrollBehavior: "auto !important",
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 24,
          minHeight: 40,
          paddingInline: 20,
          transition: "all .18s cubic-bezier(0.16, 1, 0.3, 1)",
        },
        contained: {
          backgroundColor: c.primary,
          color: c.onPrimary,
          "&:hover": {
            backgroundColor: "#ff4f72",
            boxShadow: `0 4px 18px ${c.primary}45`,
          },
        },
        sizeSmall: { minHeight: 36, paddingInline: 14 },
        outlined: {
          borderColor: c.outline,
          "&:hover": { borderColor: c.primary, backgroundColor: `${c.primary}18` },
        },
        text: { "&:hover": { backgroundColor: `${c.primary}14` } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 40,
          minHeight: 40,
          transition: "all .15s ease",
          "&:hover": { backgroundColor: `${c.onSurface}14`, transform: "scale(1.05)" },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: c.containerHigh,
          transition: "border-color .15s ease",
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: c.outline },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: c.primary },
        },
        notchedOutline: { borderColor: c.outlineVariant },
        input: { paddingBlock: 12 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: c.containerLow,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: c.containerLow,
          border: `1px solid ${c.outlineVariant}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 10, fontWeight: 500 },
        outlined: { borderColor: c.outlineVariant },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 48 },
        indicator: { height: 3, borderRadius: "3px 3px 0 0", backgroundColor: c.primary },
      },
    },
    MuiTab: { styleOverrides: { root: { minHeight: 48, textTransform: "none", fontWeight: 600 } } },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          backgroundColor: c.containerHigh,
          border: `1px solid ${c.outlineVariant}`,
          backdropFilter: "blur(20px)",
          padding: 6,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          minHeight: 42,
          transition: "background-color .15s ease",
          "&.Mui-selected": { backgroundColor: c.primaryContainer, color: c.onPrimaryContainer },
          "&:hover": { backgroundColor: `${c.onSurface}10` },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
          backgroundColor: c.containerHigh,
          border: `1px solid ${c.outlineVariant}`,
        },
      },
    },
    MuiAlert: { styleOverrides: { root: { borderRadius: 14 } } },
    MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 8, backgroundColor: c.containerHighest } } },
  },
});
